import { emitter } from '@/helpers/emitter';
import { socketClient } from '@/core/node-client';
import { economicCalendarHelperFunctions } from '../helpers/economic-calendar';

export class EconomicCalendar {
  constructor() {
    this.lastConnected = new Date();

    // Class state
    this.state = {
      startts: 0,
      endts: 0,
      headerCount: 0,
      filter: {
        impact: -1,
        country: '',
      },
      tableHeight: 0,
      currentDescriptionId: 0,
    };

    // Class methods binding
    this.insert = this.insert.bind(this);
    this.update = this.update.bind(this);
    this.updateTimeLeft = this.updateTimeLeft.bind(this);
    this.buildHeader = this.buildHeader.bind(this);
    this.filterEvents = this.filterEvents.bind(this);
    this.filterList = this.filterList.bind(this);
    this.eventDescription = this.eventDescription.bind(this);
    this.joinEconomicCalendar = this.joinEconomicCalendar.bind(this);
    this.loadEconomicCalendar = this.loadEconomicCalendar.bind(this);
    this.receiveEconomicData = this.receiveEconomicData.bind(this);

    emitter.listen('socket:econEventDescription', this.eventDescription);
    emitter.listen('socket:econFilterEvents', this.filterEvents);
    emitter.listen('socket:econFilterList', this.filterList);
    emitter.listen('socket:econReceiveCalendarData', this.receiveEconomicData);

    if ((new Date().getTime() - this.lastConnected.getTime()) / 60000 > 15) {
      this.loadEconomicCalendar(this.state.startts, this.state.endts, 1);
    }
  }

  insert(data, row) {
    if (data.id) {
      let impactClass, impactText;
      switch (data.impact.toUpperCase()) {
        case 'NONE':
          impactClass = 'none';
          impactText = 'No Impact';
          break;
        case 'LOW':
          impactClass = 'low';
          impactText = 'Low';
          break;
        case 2:
          impactClass = 'medium-low';
          impactText = 'Medium Low';
          break;
        case 'MEDIUM':
          impactClass = 'medium';
          impactText = 'Medium';
          break;
        case 4:
          impactClass = 'high';
          impactText = 'High';
          break;
        case 'HIGH':
          impactClass = 'very-high';
          impactText = 'High';
          break;
        default:
          impactClass = '';
          break;
      }
      const g = new Date(0);
      const f = new Date(0);
      const currentTime = g.setUTCSeconds(new Date().getTime()) / 1000;
      const eventTime =
        f.setUTCSeconds(new Date(data.timestamp * 1000).valueOf()) / 1000;
      if (currentTime > eventTime) {
        row.className = 'past';
      }
      row.classList.add('economic-calendar-table__row');

      const d = new Date(0);
      const date = new Date(
        d.setUTCSeconds(new Date(data.timestamp).valueOf())
      );

      let cell;
      cell = row.insertCell(0);
      cell.innerHTML = `${economicCalendarHelperFunctions.monthName(
        date.getMonth()
      )} ${`0${date.getDate()}`.slice(-2)} ${date.getHours()}:${
        date.getMinutes() < 10
          ? `0${date.getMinutes()}`
          : `${date.getMinutes()}`
      }`;
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(1);
      cell.innerHTML =
        currentTime < eventTime
          ? economicCalendarHelperFunctions.timeDifference(
              eventTime,
              currentTime
            )
          : `<svg width="14" height="14" class="calendar-check"><use xlink:href="#sprite-check-simple"></use></svg>`;
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(2);
      cell.innerHTML = `<img class="img" src ="${
        httApp.cdnUrl
      }/static/img/flags/iso2/${data.country.toLowerCase()}.png"> <span class="tooltip--triangle text-secondary" data-tooltip="${
        data.country
      }"></span>`;
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(3);
      cell.innerHTML = data.event;
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(4);
      cell.innerHTML = data.actual
        ? economicCalendarHelperFunctions.bigNumber(data.actual)
        : '-';
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(5);
      cell.innerHTML = data.consensus
        ? economicCalendarHelperFunctions.bigNumber(data.consensus)
        : '-';
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(6);
      cell.innerHTML = data.prev
        ? economicCalendarHelperFunctions.bigNumber(data.prev)
        : '-';
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(7);
      cell.innerHTML = data.unit;
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(8);
      cell.innerHTML = `<div class="impact ${impactClass}"><span class="tooltip--triangle text-secondary" data-tooltip="${impactText}"></span></div>`;
      cell.classList.add('economic-calendar-table__cell');

      cell = row.insertCell(9);
      cell.innerHTML =
        currentTime > eventTime
          ? ''
          : `<i id="btnAddevent-${data.id}" class="icon icon-icon-bell pointer" onclick="window.location.href='/ics-economic-calendar.php?id=${data.id}&title=${data.event}&description=${data.event}&datestart=${data.timestamp}&dateend=${data.timestamp}'"></i>`;
      cell.classList.add('economic-calendar-table__cell');

      row.setAttribute('data-updatedat', data.updatedAt);
      row.setAttribute('data-timestamp', eventTime);
      row.setAttribute('data-impact', data.impact);
      row.setAttribute('data-country', data.country);

      if (data.actual) {
        data.actual > data.prev
          ? row.cells[4].classList.add('higher')
          : data.actual == data.prev
          ? row.cells[4].classList.add('same')
          : row.cells[4].classList.add('lower');
      }

      if (
        this.state.filter.impact != -1 &&
        data.impact != this.state.filter.impact
      ) {
        row.style.display = 'none';
      }
    }
  }

  update(data, row) {
    if (data.id && data.actual) {
      data.actual > data.prev
        ? row.cells[4].classList.add('higher')
        : data.actual == data.prev
        ? row.cells[4].classList.add('same')
        : row.cells[4].classList.add('lower');
      row.cells[4].innerHTML = data.actual
        ? economicCalendarHelperFunctions.bigNumber(data.actual)
        : '-';
      row.cells[5].innerHTML = data.consensus
        ? economicCalendarHelperFunctions.bigNumber(data.consensus)
        : '-';
      row.setAttribute('data-updatedat', data.updatedAt);
      row.classList.add('updated');
      window.setTimeout(function () {
        row.classList.remove('updated');
      }, 5000);
    }
  }

  updateTimeLeft(row) {
    const g = new Date(0);
    const currentTime = g.setUTCSeconds(new Date().getTime()) / 1000;

    const eventTime = parseInt(row.dataset.timestamp);
    row.cells[1].innerHTML =
      currentTime < eventTime
        ? economicCalendarHelperFunctions.timeDifference(eventTime, currentTime)
        : `<i class="icon icon-check-simple"></i>`;

    if (eventTime - currentTime < 60000 && eventTime - currentTime > 0) {
      row.classList.add('updated');
    } else if (currentTime - eventTime < 60000) {
      row.classList.remove('updated');
    }

    // If event less than 5mins away
    if (eventTime - currentTime < 600000 && eventTime - currentTime > 0) {
      if (!row.classList.contains('closing')) {
        //countdown every second if < 1min left
        const updateSeconds = setInterval(() => {
          if (
            eventTime - new Date(0).setUTCSeconds(new Date().getTime()) / 1000 <
            0
          ) {
            clearInterval(updateSeconds);
          }
          this.updateTimeLeft(row);
        }, 1000);
      }
      row.classList.add('closing');
    } else if (currentTime - eventTime > 120000) {
      row.classList.remove('closing');
    }

    if (eventTime < currentTime) {
      const addCalendarBtn = document.getElementById(`btnAdd${row.id}`);
      row.classList.add('past');
      if (addCalendarBtn) {
        addCalendarBtn.style.display = 'none';
      }
    }
  }

  buildHeader(n, pageLoad) {
    const removeElements = (elms) => elms.forEach((el) => el.remove());
    removeElements(document.querySelectorAll('.date'));
    const day0 = new Date();
    const header = document.querySelector('.date-switcher-left');
    let returnts = {};
    if (n == 0) {
      for (let i = 2; i > -5; i--) {
        const anchor = document.createElement('a');
        // anchor.classList.add('date-header');
        anchor.setAttribute('id', `date-${i}`);
        const datets = new Date(day0.getTime() + 86400000 * i);
        const dayString = datets.toDateString();
        const startts = economicCalendarHelperFunctions.startts(datets);
        let endts = 0;
        anchor.setAttribute('data-startts', startts);

        if (i == 2) {
          anchor.innerHTML = 'Next 7 days';
          endts = startts + 86400 * 7;
          anchor.setAttribute('data-endts', endts);
        } else {
          endts = startts + 86400;
          anchor.innerHTML = dayString.substr(0, 10);
          anchor.setAttribute('data-endts', endts);
        }
        anchor.classList.add('date');
        if (pageLoad) {
          if (i == 0) {
            anchor.classList.add('active');
            returnts = {
              startts: startts,
              endts: endts,
            };
          }
        } else {
          if (i == -4) {
            anchor.classList.add('active');
            returnts = {
              startts: startts,
              endts: endts,
            };
          }
        }
        header.parentNode.insertBefore(anchor, header.nextSibling);
      }
    } else {
      let counter = 0;
      for (let i = -5 * n; i > -12 * n; i--) {
        counter++;
        if (counter > 7) {
          continue;
        }
        const anchor = document.createElement('a');
        const datets = new Date(day0.getTime() + 86400000 * i);
        const dayString = datets.toDateString();
        const startts = economicCalendarHelperFunctions.startts(datets);
        const endts = startts + 86400;

        anchor.setAttribute('id', `date-${i}`);
        anchor.setAttribute('data-startts', startts);
        anchor.innerHTML = dayString.substr(0, 10);
        anchor.setAttribute('data-endts', endts);
        anchor.classList.add('date');

        if (i == -5 * n) {
          anchor.classList.add('active');
          returnts = {
            startts: startts,
            endts: endts,
          };
        }
        header.parentNode.insertBefore(anchor, header.nextSibling);
      }
    }
    const dateBtns = document.querySelectorAll('.date');
    dateBtns.forEach((el) =>
      el.addEventListener('click', () => {
        this.loadEconomicCalendar(el.dataset.startts, el.dataset.endts);
        document
          .querySelectorAll('.date')
          .forEach((obj) => obj.classList.remove('active'));
        document.getElementById(el.id).classList.add('active');
      })
    );
    return returnts;
  }

  filterEvents() {
    const events = document.querySelectorAll('[id ^= "event-"]');

    // Get all selected impact values
    const selectedImpacts = Array.from(
      document.querySelectorAll('input[name="filter-impact-check"]:checked')
    ).map((el) => el.value);

    let hasFilteredEvents = false;
    events.forEach((el) => {
      el.style.display = '';
      const impactMatch =
        selectedImpacts.length === 0 ||
        selectedImpacts.includes(el.dataset.impact) ||
        selectedImpacts.includes('-1');
      const countryMatch =
        this.state.filter.country === '' ||
        el.dataset.country === this.state.filter.country;

      if (!impactMatch || !countryMatch) {
        el.style.display = 'none';
      } else {
        hasFilteredEvents = true;
      }
    });
    const table = document.querySelector('#calendarBody');
    const emptyRow = table.querySelector('.no-events-row');
    if (!hasFilteredEvents) {
      if (!emptyRow && table) {
        const row = table.insertRow(0);
        row.innerHTML =
          '<td colspan="10" class="economic-calendar-table__row no-events-row" style="width: 100%">No events based on filters</td>';
      }
    } else if (emptyRow) {
      emptyRow.remove();
    }
  }

  filterList(row) {
    const compiled = `<img class="img" src ="${
      httApp.cdnUrl
    }/static/img/flags/iso2/${row.code.toLowerCase()}.png"> ${row.code}`;
    return compiled;
  }

  eventDescription({ payload }) {
    const tableRef = document.querySelector('#calendarBody');
    const description = document.querySelector('.js_eventDescription');

    // Access the event data from payload.data
    const eventData = payload.data;

    //If we're opening the same description just close it
    if (this.state.currentDescriptionId === eventData.id) {
      this.state.currentDescriptionId = 0;
      if (description) {
        description.remove();
      }
    } else {
      this.state.currentDescriptionId = eventData.id;
      if (description) {
        description.remove();
      }

      const eventRow = document.getElementById(`event-${eventData.id}`);
      if (eventRow) {
        const rowIndex = eventRow.rowIndex;
        const newRow = tableRef.insertRow(rowIndex);
        const newTD = newRow.insertCell(0);
        // Access description directly from eventData
        newTD.innerHTML = `${eventData.description}`;
        newTD.colSpan = 10;
        newTD.classList.add('description');
        newTD.classList.add('economic-calendar-table__cell');
        newRow.classList.add('js_eventDescription');
        newRow.classList.add('event-description-row');
        newRow.classList.add('economic-calendar-table__row');
      }
    }
  }

  joinEconomicCalendar() {
    const tableRef = document.querySelector('#calendarBody');
    const date = new Date();
    this.state.startts = economicCalendarHelperFunctions.startts(date);
    this.state.endts = economicCalendarHelperFunctions.endts(date);

    setTimeout(() => {
      socketClient.joinEconomicCalendar();
    }, 500);

    setTimeout(() => {
      socketClient
        .economicCalendarEvents(this.state.startts, this.state.endts, 1000)
        .then((data) => {
          while (tableRef.hasChildNodes()) {
            tableRef.removeChild(tableRef.lastChild);
          }
          for (let i = 0; i < data.length; i++) {
            if (!document.getElementById(`event-${data[i].id}`)) {
              const row = tableRef.insertRow(0);
              row.id = `event-${data[i].id}`;
              this.insert(data[i], row);
              // Add both click and touchend for mobile compatibility
              const handleEventClick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                socketClient.eventDetails(data[i].id);
              };

              row.addEventListener('click', handleEventClick);
              row.addEventListener('touchend', handleEventClick);

              // Add touch-friendly styling
              row.style.WebkitTapHighlightColor = 'transparent';
              row.style.touchAction = 'manipulation';
            }
            this.state.tableHeight = tableRef.offsetHeight;
          }
        });
    }, 500);
    setTimeout(() => {
      const filterCountries = document.querySelector('.filter-countries');
      socketClient.economicCalendarCountries().then((data) => {
        for (let i = 0; i < data.length; i++) {
          if (!document.getElementById(`filter-${data[i].code}`)) {
            const html = this.filterList(data[i]);
            const newElement = document.createElement('li');
            newElement.id = `filter-${data[i].code}`;
            newElement.classList.add('filter-country', 'pointer');
            newElement.setAttribute('data-country', data[i].code);
            newElement.innerHTML = html;
            filterCountries.appendChild(newElement);
          }
        }
        const btnFilterCountry = document.querySelectorAll('.filter-country');
        btnFilterCountry.forEach((el) =>
          el.addEventListener('click', () => {
            this.state.filter.country = el.dataset.country;
            this.filterEvents();
          })
        );
      });
    }, 500);
  }

  loadEconomicCalendar(startts, endts, update) {
    this.state.startts = startts;
    this.state.endts = endts;
    const tableRef = document.querySelector('#calendarBody');

    socketClient
      .economicCalendarEvents(this.state.startts, this.state.endts, 1000)
      .then((data) => {
        if (!update) {
          while (tableRef.hasChildNodes()) {
            tableRef.removeChild(tableRef.lastChild);
          }
        }
        if (data.length == 0) {
          const row = tableRef.insertRow(0);
          row.classList.add('economic-calendar-table__row');
          const cell = row.insertCell(0);
          cell.classList.add('economic-calendar-table__cell');
          cell.innerHTML = 'No events for this date';
          cell.colSpan = 10;
          cell.style.width = '100%';
        } else {
          for (let i = 0; i < data.length; i++) {
            if (!document.getElementById(`event-${data[i].id}`)) {
              const row = tableRef.insertRow(0);
              row.classList.add('economic-calendar-table__row');
              row.id = `event-${data[i].id}`;
              // Add both click and touchend for mobile compatibility
              const handleEventClick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                socketClient.eventDetails(data[i].id);
              };

              row.addEventListener('click', handleEventClick);
              row.addEventListener('touchend', handleEventClick);

              // Add touch-friendly styling
              row.style.WebkitTapHighlightColor = 'transparent';
              row.style.touchAction = 'manipulation';
              this.insert(data[i], row);
            } else {
              if (
                document.getElementById(`event-${data[i].id}`).dataset
                  .updatedat < data[i].updatedAt
              ) {
                this.update(
                  data[i],
                  document.getElementById(`event-${data[i].id}`)
                );
              }
            }
          }
        }
        this.filterEvents();
      });
  }

  receiveEconomicData(data) {
    const tableRef = document.querySelector('#calendarBody');
    //Check if we're on the date that the update came
    if (
      data.timestamp > this.state.startts &&
      data.timestamp < this.state.endts
    ) {
      if (!document.getElementById(`event-${data.id}`)) {
        const row = tableRef.insertRow(0);
        row.id = `event-${data.id}`;
        this.insert(data, row);

        // Add both click and touchend for mobile compatibility
        const handleEventClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          socketClient.eventDetails(data.id);
        };

        row.addEventListener('click', handleEventClick);
        row.addEventListener('touchend', handleEventClick);

        // Add touch-friendly styling
        row.style.WebkitTapHighlightColor = 'transparent';
        row.style.touchAction = 'manipulation';
      } else {
        if (
          document.getElementById(`event-${data.id}`).dataset.updatedat <
          data.updatedAt
        ) {
          this.update(data, document.getElementById(`event-${data.id}`));
        }
      }
    }
  }
}
